import React, { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { Download, ChevronLeft, ChevronRight } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';
const PAGE_SIZE = 50;

const PaymentsTab = ({
  paymentAttempts,
  paymentFilters,
  setPaymentFilters,
  getFilteredPayments
}) => {
  const [page, setPage] = useState(1);

  const filtered = getFilteredPayments();
  const totalPages = Math.ceil(filtered.length / PAGE_SIZE);
  const paginated = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  const handleExportCSV = () => {
    // Pass the currently-selected date filters to the backend so the CSV matches what's on screen.
    // If no filter is set, the backend returns the full history.
    const params = new URLSearchParams();
    if (paymentFilters.month) {
      params.set('month', paymentFilters.month);
    } else {
      if (paymentFilters.dateFrom) params.set('start_date', paymentFilters.dateFrom);
      if (paymentFilters.dateTo) params.set('end_date', paymentFilters.dateTo);
    }
    const qs = params.toString();
    window.open(`${API_URL}/api/admin/payments/export-csv${qs ? `?${qs}` : ''}`, '_blank');
  };

  return (
    <div className="space-y-6">
      <Card className="bg-white">
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="text-gray-900">All Subscribers & Payment Attempts</CardTitle>
              <CardDescription className="text-gray-600">
                Complete payment history from inception - all transactions
              </CardDescription>
            </div>
            <Button size="sm" onClick={handleExportCSV} className="bg-green-600 hover:bg-green-700 text-white" data-testid="export-csv-btn">
              <Download className="w-4 h-4 mr-1" />
              {paymentFilters.month || paymentFilters.dateFrom || paymentFilters.dateTo
                ? 'Export Filtered CSV'
                : 'Export All CSV'}
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {paymentAttempts && (
            <div className="mb-4 flex space-x-4">
              <Badge variant="default" className="bg-green-100 text-green-800">
                Completed: {paymentAttempts.completed_payments}
              </Badge>
              <Badge variant="destructive">
                Failed: {paymentAttempts.failed_payments}
              </Badge>
              <Badge variant="outline" className="border-gray-300 text-gray-700">
                Total: {paymentAttempts.total_attempts}
              </Badge>
            </div>
          )}

          {/* Payment Filters */}
          <div className="mb-6 p-4 bg-gray-50 rounded-lg border border-gray-200">
            <h3 className="text-sm font-semibold text-gray-900 mb-3">Filter Payments</h3>
            <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
              <div>
                <Label htmlFor="service-filter" className="text-gray-700 text-xs mb-1 block">Service Type</Label>
                <select
                  id="service-filter"
                  value={paymentFilters.serviceType || 'all'}
                  onChange={(e) => { setPaymentFilters({...paymentFilters, serviceType: e.target.value}); setPage(1); }}
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="all">All Services</option>
                  <option value="options_only">Options Only</option>
                  <option value="complete_package">Complete Package</option>
                  <option value="equity_only">Equity Only</option>
                  <option value="workshop">Workshop</option>
                  <option value="automated_alerts_1day">Automated Alerts (1 Day)</option>
                  <option value="automated_alerts_3day">Automated Alerts (3 Day)</option>
                  <option value="automated_alerts_5day">Automated Alerts (5 Day)</option>
                </select>
              </div>
              <div>
                <Label htmlFor="status-filter" className="text-gray-700 text-xs mb-1 block">Payment Status</Label>
                <select
                  id="status-filter"
                  value={paymentFilters.status}
                  onChange={(e) => { setPaymentFilters({...paymentFilters, status: e.target.value}); setPage(1); }}
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md"
                >
                  <option value="all">All Payments</option>
                  <option value="completed">Completed</option>
                  <option value="pending">Pending</option>
                  <option value="failed">Failed</option>
                </select>
              </div>
              <div>
                <Label className="text-gray-700 text-xs mb-1 block">Date From</Label>
                <Input
                  type="date"
                  value={paymentFilters.dateFrom}
                  onChange={(e) => { setPaymentFilters({...paymentFilters, dateFrom: e.target.value, month: ''}); setPage(1); }}
                  className="text-sm"
                />
              </div>
              <div>
                <Label className="text-gray-700 text-xs mb-1 block">Date To</Label>
                <Input
                  type="date"
                  value={paymentFilters.dateTo}
                  onChange={(e) => { setPaymentFilters({...paymentFilters, dateTo: e.target.value, month: ''}); setPage(1); }}
                  className="text-sm"
                />
              </div>
              <div>
                <Label className="text-gray-700 text-xs mb-1 block">Quick Month</Label>
                <Input
                  type="month"
                  value={paymentFilters.month}
                  onChange={(e) => { setPaymentFilters({...paymentFilters, month: e.target.value, dateFrom: '', dateTo: ''}); setPage(1); }}
                  className="text-sm"
                />
              </div>
            </div>
            <div className="flex items-center gap-3 mt-3">
              <Button
                size="sm" variant="outline"
                onClick={() => { setPaymentFilters({ serviceType: 'all', status: 'all', dateFrom: '', dateTo: '', month: '' }); setPage(1); }}
                className="text-xs"
              >
                Clear Filters
              </Button>
              <Badge variant="outline" className="border-blue-300 text-blue-700">
                Showing {paginated.length} of {filtered.length} payments (Page {page}/{totalPages || 1})
              </Badge>
            </div>
          </div>
          
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="payments-table">
              <thead>
                <tr className="border-b border-gray-200">
                  <th className="text-left py-2 px-3 font-medium text-gray-700">Customer</th>
                  <th className="text-left py-2 px-3 font-medium text-gray-700">Service</th>
                  <th className="text-left py-2 px-3 font-medium text-gray-700">Amount</th>
                  <th className="text-left py-2 px-3 font-medium text-gray-700">Status</th>
                  <th className="text-left py-2 px-3 font-medium text-gray-700">Date</th>
                  <th className="text-left py-2 px-3 font-medium text-gray-700">Coupon</th>
                  <th className="text-left py-2 px-3 font-medium text-gray-700">Payment ID</th>
                </tr>
              </thead>
              <tbody>
                {paginated.map((attempt, index) => (
                  <tr key={index} className="border-b border-gray-100 hover:bg-gray-50">
                    <td className="py-2 px-3">
                      <div>
                        <p className="font-medium text-gray-900">{attempt.customer_name}</p>
                        <p className="text-xs text-gray-600">{attempt.customer_email}</p>
                        <p className="text-xs text-gray-600">{attempt.customer_phone}</p>
                      </div>
                    </td>
                    <td className="py-2 px-3">
                      <Badge variant="outline" className="border-gray-300 text-gray-700">
                        {attempt.service_type}
                      </Badge>
                    </td>
                    <td className="py-2 px-3">
                      <p className="font-medium text-gray-900">₹{attempt.amount}</p>
                      {attempt.discount_amount > 0 && (
                        <p className="text-xs text-green-600">-₹{attempt.discount_amount}</p>
                      )}
                    </td>
                    <td className="py-2 px-3">
                      <Badge 
                        variant={attempt.status === 'completed' ? 'default' : attempt.status === 'failed' ? 'destructive' : 'secondary'}
                        className={attempt.status === 'completed' ? 'bg-green-100 text-green-800' : ''}
                      >
                        {attempt.status}
                      </Badge>
                    </td>
                    <td className="py-2 px-3 text-gray-600">
                      {attempt.attempted_at ? new Date(attempt.attempted_at).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : 'N/A'}
                    </td>
                    <td className="py-2 px-3">
                      {attempt.coupon_used && (
                        <Badge variant="outline" className="border-orange-300 text-orange-700">
                          {attempt.coupon_used}
                        </Badge>
                      )}
                    </td>
                    <td className="py-2 px-3 text-xs text-gray-500 font-mono">
                      {attempt.razorpay_payment_id || '-'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between mt-4 pt-4 border-t">
              <Button size="sm" variant="outline" disabled={page <= 1} onClick={() => setPage(p => p - 1)} data-testid="prev-page-btn">
                <ChevronLeft className="w-4 h-4 mr-1" /> Previous
              </Button>
              <div className="flex items-center gap-1">
                {Array.from({ length: Math.min(totalPages, 7) }, (_, i) => {
                  let pageNum;
                  if (totalPages <= 7) pageNum = i + 1;
                  else if (page <= 4) pageNum = i + 1;
                  else if (page >= totalPages - 3) pageNum = totalPages - 6 + i;
                  else pageNum = page - 3 + i;
                  return (
                    <button
                      key={pageNum}
                      onClick={() => setPage(pageNum)}
                      className={`w-8 h-8 text-xs rounded ${page === pageNum ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}
                    >
                      {pageNum}
                    </button>
                  );
                })}
              </div>
              <Button size="sm" variant="outline" disabled={page >= totalPages} onClick={() => setPage(p => p + 1)} data-testid="next-page-btn">
                Next <ChevronRight className="w-4 h-4 ml-1" />
              </Button>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default PaymentsTab;
